import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useStore } from "@/hooks/useStore";
import { useProducts } from "@/hooks/useProducts";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { ProductForm } from "@/components/products/ProductForm";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import type { ProductFormData, ProductSize } from "@/types/product";

interface ProductOccasion {
  id: string;
  name: string;
}

export default function EditProduct() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, isFlorist, loading: authLoading } = useAuth();
  const { data: store, isLoading: storeLoading } = useStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { products, updateProduct: updateProductMutation, isLoading: productsLoading } = useProducts({
    storeId: store?.id || "",
    enabled: !authLoading && !!user && !!isFlorist && !!store?.id,
  });

  const product = products?.find(p => p.id === id);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!user || !isFlorist) {
      navigate("/");
      return;
    }
  }, [user, isFlorist, authLoading, navigate, product, productsLoading]);

  if (authLoading || storeLoading || (productsLoading && !product)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-gray-900">Product Not Found</h1>
          <p className="mt-2 text-gray-600">The product you're looking for doesn't exist.</p>
          <Button
            onClick={() => navigate("/products")}
            className="mt-4"
          >
            Back to Products
          </Button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (data: ProductFormData) => {
    try {
      setIsSubmitting(true);
      
      console.log("Starting product update submission", {
        id: product.id,
        title: data.title,
        sizesCount: data.sizes.length,
        firstSize: {
          name: data.sizes[0]?.size_name,
          price: data.sizes[0]?.price,
          hasImage: !!data.sizes[0]?.image_url
        }
      });
      
      // Pre-validate the data to catch issues early
      // Make sure sizes are valid before submitting
      const invalidSize = data.sizes.find(size => !size.size_name || typeof size.price !== 'number' || size.price <= 0);
      if (invalidSize) {
        console.error("Invalid size detected:", invalidSize);
        throw new Error("All sizes must have a name and valid price");
      }

      // Image upload is optional - products can be saved without images

      // Added check for size_name
      const missingNameSize = data.sizes.find(size => !size.size_name);
      if (missingNameSize) {
        console.error("Size missing name:", missingNameSize);
        throw new Error("All sizes must have a name");
      }

      // Map occasions with their IDs
      const mappedOccasions = data.occasions.map(name => {
        const existingOccasion = product.occasions.find((o: ProductOccasion) => o.name === name);
        return {
          id: existingOccasion?.id || "",
          name,
        };
      });

      // Get the main image from the first size (may be undefined if no image uploaded)
      const mainImageUrl = data.sizes[0]?.image_url || null;

      console.log("Submitting product update with validated data", {
        mainImageUrl: mainImageUrl?.substring(0, 30) + '...',
        sizesCount: data.sizes.length
      });

      try {
        console.log("Final product data to update:", {
          id: product.id,
          title: data.title,
          sizes: data.sizes.map(size => ({
            id: size.id,
            size_name: size.size_name,
            price: size.price
          }))
        });
        
        const result = await updateProductMutation({
          id: product.id,
          title: data.title,
          description: data.description,
          category: data.category,
          active: data.active,
          image_url: mainImageUrl, // Use first size's image as main image
          sizes: data.sizes.map(size => ({
            id: size.id,
            size_name: size.size_name,
            price: size.price,
            image_url: size.use_product_image ? null : size.image_url,
            use_product_image: !!size.use_product_image
          })),
          occasions: mappedOccasions,
        });

        console.log("Product update result:", result);
        toast.success("Product updated successfully");
        setIsSubmitting(false);
      } catch (updateError) {
        console.error("Error from updateProduct:", updateError);
        // Rethrow to be caught by the outer catch block
        throw updateError;
      }
    } catch (error: any) {
      console.error("Error updating product:", error);
      setIsSubmitting(false);
      
      // Show toast with error message
      toast.error(error.message || "Failed to update product");
      
      // Throw the error to be caught by the form's error handling
      throw error;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/products")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">Edit Product</h1>
        </div>

        {/* Show loading state */}
        {isSubmitting && (
          <div className="mb-4 p-2 bg-primary/10 rounded flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Saving product changes...</span>
          </div>
        )}

        <ProductForm
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          initialData={{
            id: product.id,
            title: product.title,
            description: product.description,
            category: product.category,
            active: product.active,
            occasions: product.occasions.map((o: ProductOccasion) => o.name),
            sizes: product.sizes.map((s: ProductSize, index: number) => {
              // For debugging only
              console.log(`Size ${index} (${s.size_name}) image sources:`, { 
                main_image_url: s.main_image_url, 
                image_url: s.image_url,
                product_main_image: product.main_image_url,
                product_image_url: product.image_url,
                size_name: s.size_name  // Explicitly log size_name
              });
              
              // Determine the correct image URL for this size
              let imageUrl = null;
              // For the first size (main product image), use the product's main image if available
              if (index === 0) {
                imageUrl = product.image_url || product.main_image_url || s.image_url || s.main_image_url || null;
              } else {
                // For other sizes, use their specific image if not using the main product image
                if (s.use_main_product_image || s.use_product_image) {
                  // If using main product image, reference the first size's image
                  imageUrl = product.image_url || product.main_image_url || product.sizes[0]?.image_url || product.sizes[0]?.main_image_url || null;
                } else {
                  // Otherwise use this size's specific image
                  imageUrl = s.image_url || s.main_image_url || null;
                }
              }
              
              // Ensure size_name is not null
              const sizeName = s.size_name || `Size ${index + 1}`;
              console.log(`Mapped size ${index}: ${sizeName}`);
              
              return {
                id: s.id,
                size_name: sizeName,
                price: s.price,
                image_url: imageUrl,
                use_product_image: s.use_main_product_image || s.use_product_image || false,
              };
            }),
          }}
        />
      </div>
    </div>
  );
} 